import React from "react"
import { MuiButton, MuiCard, MuiChip } from "@ace/ace-ui-web"
import { LocationPin, AccessTime, CalendarMonth, Person, HourglassEmpty, CheckCircle, Cancel, Warning } from "@mui/icons-material"
import { motion } from "framer-motion"
import { Dialog, Typography, Stack, Box } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { normalizeShiftType } from "../../utils/shiftTypeUtils"
import { parseShiftId } from "../../utils/shiftUtils"
import styles from "./shiftcard.module.css"

// Dual approval status for WKR shifts
export type ApprovalStatus = "pending" | "approved" | "denied";

export interface DualApprovalInfo {
    posterSupervisorId?: string;
    posterSupervisorName?: string;
    posterSupervisorApprovalStatus?: ApprovalStatus;
    claimerSupervisorId?: string;
    claimerSupervisorName?: string;
    claimerSupervisorApprovalStatus?: ApprovalStatus;
}

export type Shift = {
    id: string
    requestId?: number | string
    requestSeqId?: number | string
    title: string
    shiftType?: string
    location: string
    date: string
    dateRangeLabel?: string
    startTime: string
    endTime: string
    rate: string
    status: "APP" | "CAN" | "DEN" | "PND" | "PST"
    description?: string
    postedBy?: string
    grabbedBy?: string
    grabbedByName?: string
    claimedBy?: string
    claimedById?: string
    claimedByName?: string
    availableCount?: number
    notes?: string
    rejectionNotes?: string
    isGigShift?: boolean
    totalDays?: number
    gigDays?: Array<{
        requestSeqId: number | string
        date: string
        hours: number | null
        status: Shift["status"]
    }>
    // New fields for WKR dual approval
    dualApproval?: DualApprovalInfo
}

// Loose shape accepted by the card. Mirrors the availableShifts API payload.
export type RawShift = Partial<Shift> & {
    requestId?: number | string
    shiftId?: string
    requestShiftId?: string

    shiftDesc?: string | null
    skill?: string
    skillDesc?: string
    requiredSkillCd?: string
    requiredSkillDesc?: string

    siteCd?: string
    siteName?: string

    requestDt?: string
    requestDate?: string

    requestType?: string
    requestNotes?: string
    requiredEmployeeCount?: number | string
    requiredTeamCd?: string

    postedByUserNm?: string
    postByName?: string

    claimedByUserNm?: string
    claimByName?: string

    isGigShift?: boolean
    dateRangeLabel?: string
    totalDays?: number
    gigDays?: Shift["gigDays"]

    openSlots?: number
    pendingSlots?: number

    // New fields for WKR dual approval (API response shape)
    posterSupervisorId?: string
    posterSupervisorName?: string
    posterSupervisorApprovalStatus?: ApprovalStatus
    claimerSupervisorId?: string
    claimerSupervisorName?: string
    claimerSupervisorApprovalStatus?: ApprovalStatus
}

export type ShiftAction = "reject" | "approve" | "grab"

const REJECTION_NOTES_MAX_LENGTH = 500

interface ShiftCardProps {
    shift: RawShift
    onHandleConfirm?: (shift: Shift, action: ShiftAction, rejectionNotes?: string) => void
    isApproveReject?: boolean
    readOnly?: boolean
}

// ---------- helpers ----------
const toDateOnly = (value?: string): string => {
    if (!value) return ""
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value)
    if (match) return match[1]
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ""
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
}

const toTimeOnly = (value?: string): string => {
    if (!value) return ""
    if (/^\d{2}:\d{2}/.test(value)) return value.slice(0, 5)
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ""
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

const deriveStatus = (s: RawShift): Shift["status"] => s.status ?? "PST"

const toAvailableCount = (s: RawShift): number => {
    if (typeof s.openSlots === "number") return s.openSlots
    if (typeof s.availableCount === "number") return s.availableCount
    if (typeof s.requiredEmployeeCount === "string") return Number.parseInt(s.requiredEmployeeCount, 10) || 0
    if (typeof s.requiredEmployeeCount === "number") return s.requiredEmployeeCount
    return 0
}

// If a shift was cancelled, infer why based on whether someone had claimed it.
// claimedById / grabbedBy populated → was pending approval (PND) → "Expired - Not Approved"
// nothing populated → was never claimed (PST) → "Expired - Not Claimed"
const getCancellationReason = (shift: Shift): string | null => {
    if (shift.status !== "CAN") return null
    return shift.claimedById || shift.grabbedBy
        ? "Expired - Not Approved"
        : "Expired - Not Claimed"
}

// Helper to check if this is a WKR shift that needs dual approval display
const isWkrShiftWithDualApproval = (shift: Shift): boolean => {
    return shift.shiftType === "WKR" && shift.dualApproval !== undefined
}

// Helper to get the display label for approval status
const getApprovalStatusLabel = (status?: ApprovalStatus): string => {
    switch (status) {
        case "approved": return "Approved"
        case "denied": return "Denied"
        case "pending":
        default: return "Awaiting"
    }
}

// Helper to get the overall dual approval header text and icon
const getDualApprovalHeader = (dualApproval: DualApprovalInfo): { text: string; variant: "warning" | "success" | "error" } => {
    const posterStatus = dualApproval.posterSupervisorApprovalStatus
    const claimerStatus = dualApproval.claimerSupervisorApprovalStatus

    if (posterStatus === "denied" || claimerStatus === "denied") {
        return { text: "Approval Denied", variant: "error" }
    }
    if (posterStatus === "approved" && claimerStatus === "approved") {
        return { text: "Both Supervisors Approved", variant: "success" }
    }
    return { text: "Dual Approval Required", variant: "warning" }
}

const normalizeShift = (s: RawShift): Shift => ({
    id: String(s.id ?? s.requestId ?? s.shiftId ?? s.requestShiftId ?? ""),
    requestId: s.requestId,
    requestSeqId: s.requestSeqId,
    title: s.title ??
            s.shiftDesc ??
            s.requiredSkillDesc ??
            s.skillDesc ??
            s.skill ??
            s.requiredSkillCd ??
            "",
    shiftType: s.shiftType ?? s.requestType,
    location: s.location ?? s.siteName ?? s.siteCd ?? "",
    date: toDateOnly(s.date ?? s.requestDt ?? s.requestDate),
    dateRangeLabel: s.dateRangeLabel,
    startTime: toTimeOnly(s.startTime),
    endTime: toTimeOnly(s.endTime),
    rate: s.rate ?? "",
    status: deriveStatus(s),
    description: s.description ?? s.shiftDesc ?? undefined,
    postedBy: s.postedBy ?? s.postedByUserNm ?? s.postByName,
    grabbedBy: s.grabbedBy,
    grabbedByName: s.grabbedByName,
    claimedBy: s.claimedBy ?? s.claimedByName ?? s.claimedByUserNm ?? s.claimByName ?? s.grabbedByName,
    claimedById: s.claimedById,
    availableCount: toAvailableCount(s),
    notes: s.notes ?? s.requestNotes,
    rejectionNotes: s.rejectionNotes,
    isGigShift: s.isGigShift,
    totalDays: s.totalDays,
    gigDays: s.gigDays,
    // Map dual approval fields if present
    dualApproval: s.posterSupervisorApprovalStatus ? {
        posterSupervisorId: s.posterSupervisorId,
        posterSupervisorName: s.posterSupervisorName,
        posterSupervisorApprovalStatus: s.posterSupervisorApprovalStatus,
        claimerSupervisorId: s.claimerSupervisorId,
        claimerSupervisorName: s.claimerSupervisorName,
        claimerSupervisorApprovalStatus: s.claimerSupervisorApprovalStatus,
    } : undefined,
})

// ---------- Dual Approval Status Component ----------
interface DualApprovalStatusProps {
    dualApproval: DualApprovalInfo
}

const DualApprovalStatus: React.FC<DualApprovalStatusProps> = ({ dualApproval }) => {
    const header = getDualApprovalHeader(dualApproval)

    const getStatusIcon = (status?: ApprovalStatus) => {
        switch (status) {
            case "approved":
                return <CheckCircle sx={{ fontSize: 16, color: "#4caf50" }} />
            case "denied":
                return <Cancel sx={{ fontSize: 16, color: "#f44336" }} />
            case "pending":
            default:
                return <HourglassEmpty sx={{ fontSize: 16, color: "#ff9800" }} />
        }
    }

    const getStatusColor = (status?: ApprovalStatus): string => {
        switch (status) {
            case "approved": return "#4caf50"
            case "denied": return "#f44336"
            case "pending":
            default: return "#ff9800"
        }
    }

    const getHeaderIcon = () => {
        switch (header.variant) {
            case "success":
                return <CheckCircle sx={{ fontSize: 18, color: "#4caf50" }} />
            case "error":
                return <Cancel sx={{ fontSize: 18, color: "#f44336" }} />
            case "warning":
            default:
                return <Warning sx={{ fontSize: 18, color: "#ff9800" }} />
        }
    }

    const getHeaderBgColor = (): string => {
        switch (header.variant) {
            case "success": return "rgba(76, 175, 80, 0.08)"
            case "error": return "rgba(244, 67, 54, 0.08)"
            case "warning":
            default: return "rgba(255, 152, 0, 0.08)"
        }
    }

    const getHeaderBorderColor = (): string => {
        switch (header.variant) {
            case "success": return "#4caf50"
            case "error": return "#f44336"
            case "warning":
            default: return "#ff9800"
        }
    }

    return (
        <Box className={styles.dualApprovalSection}>
            <Box
                sx={{
                    backgroundColor: getHeaderBgColor(),
                    borderLeft: `3px solid ${getHeaderBorderColor()}`,
                    borderRadius: "0 6px 6px 0",
                    p: 1.5,
                }}
            >
                {/* Header */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    {getHeaderIcon()}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: getHeaderBorderColor() }}>
                        {header.text}
                    </Typography>
                </Stack>

                {/* Supervisor 1 (Poster's) */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                    <Typography variant="caption" sx={{ color: "#666", minWidth: 140 }}>
                        Poster&apos;s Supervisor{dualApproval.posterSupervisorName ? ":" : ""}
                    </Typography>
                    {dualApproval.posterSupervisorName && (
                        <Typography variant="caption" sx={{ color: "#333", fontWeight: 500 }}>
                            {dualApproval.posterSupervisorName}
                        </Typography>
                    )}
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: "auto" }}>
                        {getStatusIcon(dualApproval.posterSupervisorApprovalStatus)}
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: getStatusColor(dualApproval.posterSupervisorApprovalStatus),
                            }}
                        >
                            {getApprovalStatusLabel(dualApproval.posterSupervisorApprovalStatus)}
                        </Typography>
                    </Stack>
                </Stack>

                {/* Supervisor 2 (Claimer's) */}
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" sx={{ color: "#666", minWidth: 140 }}>
                        Claimer&apos;s Supervisor{dualApproval.claimerSupervisorName ? ":" : ""}
                    </Typography>
                    {dualApproval.claimerSupervisorName && (
                        <Typography variant="caption" sx={{ color: "#333", fontWeight: 500 }}>
                            {dualApproval.claimerSupervisorName}
                        </Typography>
                    )}
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: "auto" }}>
                        {getStatusIcon(dualApproval.claimerSupervisorApprovalStatus)}
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: getStatusColor(dualApproval.claimerSupervisorApprovalStatus),
                            }}
                        >
                            {getApprovalStatusLabel(dualApproval.claimerSupervisorApprovalStatus)}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    )
}

const ShiftCard: React.FC<ShiftCardProps> = ({
    shift: rawShift,
    onHandleConfirm,
    isApproveReject = false,
    readOnly = false,
}) => {
    const shift = React.useMemo(() => normalizeShift(rawShift), [rawShift])
    const navigate = useNavigate()

    const [action, setAction] = React.useState<ShiftAction>("grab")
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false)
    const [rejectionNotes, setRejectionNotes] = React.useState("")

    const handleShowConfirm = (a: ShiftAction) => {
        setAction(a)
        setRejectionNotes("")
        setIsConfirmOpen(true)
    }

    const handleClose = () => {
        setIsConfirmOpen(false)
        setRejectionNotes("")
    }

    const handleReviewClick = () => {
        const parsed = parseShiftId(shift.id)
        const requestId = shift.requestId ?? parsed.requestId
        const requestSeqId = shift.requestSeqId ?? parsed.requestSeqId
        const params = new URLSearchParams({ tab: 'calendar' })
        if (requestId) params.set('requestId', String(requestId))
        if (requestSeqId) params.set('seqId', String(requestSeqId))
        if (shift.date) params.set('date', shift.date)
        if (shift.isGigShift) params.set('shiftType', 'GIG')
        navigate(`/reports?${params.toString()}`)
    }

    // Determine if we should show dual approval section (only once, used throughout)
    const showDualApproval = isWkrShiftWithDualApproval(shift)

    const getStatusChip = (status: string) => {
        // For WKR shifts with dual approval, only show terminal state chips
        // The DualApprovalStatus component handles displaying the approval states
        if (showDualApproval && shift.dualApproval) {
            if (status === "CAN") return <MuiChip colors="default" label="Cancelled" />
            if (status === "DEN") return <MuiChip colors="red" label="Denied" />
            // For APP or PND status, don't render a chip - let DualApprovalStatus handle it
            return <></>
        }

        if (status === "APP") return <MuiChip colors="green" label="Approved" />
        if (status === "CAN") return <MuiChip colors="default" label="Cancelled" />
        if (status === "DEN") return <MuiChip colors="red" label="Denied" />
        if (status === "PND") return <MuiChip colors="yellow" label="Pending" />
        if (status === "PST" && (shift.availableCount ?? 0) > 0) return <MuiChip colors="blue" label="Posted" />
        return <></>
    }

    // For WKR shifts, derive overall status from dual approval
    const getShiftTypeLabel = (shiftType?: string): string => {
        const base = normalizeShiftType(shiftType)
        if (base === "WKR") return "Worker"
        if (base === "VTO") return "VTO"
        if (base === "SFT") return "Shift"
        if (base === "GIG") return "Gig"
        return base
    }

    const formattedDate = shift.date
        ? new Date(shift.date + "T00:00:00").toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        })
        : ""

    const dateLabel = shift.dateRangeLabel || formattedDate
    const totalGigHours = shift.gigDays?.reduce((sum, day) => sum + (day.hours ?? 0), 0)
    const cancellationReason = getCancellationReason(shift)

    return (
        <div className={styles.cardWrapper}>
            <motion.div
                whileHover={{ scale: 1.01 }}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
            >
                <MuiCard
                    borderRadius={2}
                    elevation={5}
                    padding={1}
                    variant="elevation"
                >
                    <div className={styles.cardHeader}>
                        <div className={styles.cardHeaderContent}>
                            <Typography variant="h6" className={styles.cardTitle}>
                                {shift.title}
                            </Typography>
                        </div>
                        <div className={styles.chipContainer}>
                            {shift.shiftType && (
                                <MuiChip colors="default" label={getShiftTypeLabel(shift.shiftType)} />
                            )}
                            {shift.isGigShift && (shift.totalDays ?? 0) > 1 && (
                                <MuiChip colors="default" label={`${shift.totalDays} Days`} />
                            )}
                            {!isApproveReject && !readOnly && shift.status === "PST" && (shift.availableCount ?? 0) > 1 && (
                                <MuiChip colors="default" label={`${shift.availableCount} Available`} />
                            )}
                            {getStatusChip(shift.status)}
                        </div>
                    </div>
                    <div className={styles.detailsGrid}>
                        <div className={styles.detailRow}>
                            <LocationPin fontSize="small" className={styles.detailIcon} />
                            <span>{shift.location}</span>
                        </div>
                        <div className={styles.detailRow}>
                            <CalendarMonth fontSize="small" className={styles.detailIcon} />
                            <span>{dateLabel}</span>
                        </div>
                        {(shift.startTime || shift.endTime) && (
                            <div className={styles.detailRow}>
                                <AccessTime fontSize="small" className={styles.detailIcon} />
                                <span>{shift.startTime} - {shift.endTime}</span>
                            </div>
                        )}
                        {shift.isGigShift && totalGigHours !== undefined && totalGigHours > 0 && (
                            <div className={styles.detailRow}>
                                <AccessTime fontSize="small" className={styles.detailIcon} />
                                <span>{totalGigHours} hours</span>
                            </div>
                        )}
                        {shift.postedBy && (
                            <div className={styles.detailRow}>
                                <Person fontSize="small" className={styles.detailIcon} />
                                <span>Posted by: {shift.postedBy}</span>
                            </div>
                        )}
                        {isApproveReject && shift.claimedBy && (
                            <div className={styles.detailRow}>
                                <Person fontSize="small" className={styles.detailIcon} />
                                <span>Claimed by: {shift.claimedBy}</span>
                            </div>
                        )}
                    </div>

                    {/* Dual Approval Section for WKR shifts */}
                    {showDualApproval && shift.dualApproval && (
                        <DualApprovalStatus dualApproval={shift.dualApproval} />
                    )}

                    <div className={styles.cardFooter}>
                        <div className={styles.notesSection}>
                            <div className={styles.notesLabel}>Notes</div>
                            <p className={styles.notesText}>{shift.notes || ""}</p>
                        </div>

                        {shift.status === "DEN" && shift.rejectionNotes && (
                            <div className={styles.deniedNotesSection}>
                                <div className={styles.deniedNotesLabel}>Rejection Notes</div>
                                <p className={styles.notesText}>{shift.rejectionNotes}</p>
                            </div>
                        )}

                        {cancellationReason && (
                            <div className={styles.deniedNotesSection}>
                                <div className={styles.deniedNotesLabel}>Cancellation Reason</div>
                                <p className={styles.notesText}>{cancellationReason}</p>
                            </div>
                        )}

                        {(!readOnly && isApproveReject) && (
                            <div className={`${styles.hrManagerButtons} ${shift.isGigShift ? styles.hrManagerButtonsLeft : ''}`}>
                                {(!shift.isGigShift) &&
                                    <MuiButton colors="secondary" sx={{ width: "33%" }} onClick={() => handleShowConfirm("reject")}>
                                        Reject
                                    </MuiButton>
                                }
                                <MuiButton colors="tertiary" variant="outlined" sx={{ width: "33%" }} onClick={handleReviewClick}>
                                    Review
                                </MuiButton>
                                {(!shift.isGigShift) &&
                                    <MuiButton colors="acePrimary" sx={{ width: "33%" }} onClick={() => handleShowConfirm("approve")}>
                                        Approve
                                    </MuiButton>
                                }
                            </div>
                        )}
                        {(!readOnly && !isApproveReject) && (
                            <div className={styles.grabShiftButton}>
                                <MuiButton onClick={() => handleShowConfirm("grab")}>
                                    {normalizeShiftType(shift.shiftType) === "VTO" ? "VTO Shift" : "Grab Shift"}
                                </MuiButton>
                            </div>
                        )}
                    </div>
                </MuiCard>
            </motion.div>
            <Dialog open={isConfirmOpen} onClose={handleClose}>
                <div>
                    {action === "approve" && (
                        <div className={styles.confirmCardHeader}>
                            <Typography variant="h5" className={styles.cardTitle}>Request Approval</Typography>
                            <p className={styles.cardSubtitle}>Please review and confirm approval</p>
                        </div>
                    )}
                    {action === "reject" && (
                        <div className={styles.confirmCardHeader}>
                            <Typography variant="h5" className={styles.cardTitle}>Request Rejection</Typography>
                            <p className={styles.cardSubtitle}>Please review and confirm rejection</p>
                        </div>
                    )}
                    {action !== "approve" && action !== "reject" && (
                        <div className={styles.confirmCardHeader}>
                            <Typography variant="h5" className={styles.cardTitle}>Confirm Shift Details</Typography>
                            <p className={styles.cardSubtitle}>Please review and confirm details</p>
                        </div>
                    )}
                    <hr style={{ border: "none", borderTop: "1px solid var(--color-border)" }} />
                    <div className={styles.confirmWrapper}>
                        <div className={styles.cardShiftHeader}>
                            <div className={styles.cardShiftTitle}>
                                <Typography variant="h6" className={styles.cardTitle}>{shift.title}</Typography>
                            </div>
                            <div className={styles.chipContainer}>
                                {shift.shiftType && <MuiChip colors="default" label={getShiftTypeLabel(shift.shiftType)} />}
                            </div>
                        </div>
                        <div className={styles.detailsGrid}>
                            <div className={styles.detailRow}>
                                <LocationPin fontSize="small" className={styles.detailIcon} />
                                <span>{shift.location}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <CalendarMonth fontSize="small" className={styles.detailIcon} />
                                <span>{dateLabel}</span>
                            </div>
                            {(shift.startTime || shift.endTime) && (
                                <div className={styles.detailRow}>
                                    <AccessTime fontSize="small" className={styles.detailIcon} />
                                    <span>{shift.startTime} - {shift.endTime}</span>
                                </div>
                            )}
                            {shift.isGigShift && totalGigHours !== undefined && totalGigHours > 0 && (
                                <div className={styles.detailRow}>
                                    <AccessTime fontSize="small" className={styles.detailIcon} />
                                    <span>{totalGigHours} total hours</span>
                                </div>
                            )}
                            {shift.postedBy && (
                                <div className={styles.detailRow}>
                                    <Person fontSize="small" className={styles.detailIcon} />
                                    <span>Posted by: {shift.postedBy}</span>
                                </div>
                            )}
                            {(action === "approve" || action === "reject") && shift.claimedBy && (
                                <div className={styles.detailRow}>
                                    <Person fontSize="small" className={styles.detailIcon} />
                                    <span>Claimed by: {shift.claimedBy}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {shift.notes ? (
                        <div className={styles.notesSection}>
                            <div className={styles.notesLabel}>Notes:</div>
                            <p className={styles.notesText}>{shift.notes}</p>
                        </div>
                    ) : (
                        <div className={styles.notesSectionEmpty}></div>
                    )}
                    {action === "reject" && (
                        <div className={styles.rejectionNotesSection}>
                            <label className={styles.notesLabel} htmlFor="rejectionNotes">
                                Rejection Notes (Optional)
                            </label>
                            <textarea
                                id="rejectionNotes"
                                className={styles.rejectionNotesTextarea}
                                placeholder="Provide a reason for rejection..."
                                value={rejectionNotes}
                                maxLength={REJECTION_NOTES_MAX_LENGTH}
                                rows={3}
                                onChange={(e) => setRejectionNotes(e.target.value)}
                            />
                            <p className={styles.rejectionNotesCounter}>
                                {rejectionNotes.length} / {REJECTION_NOTES_MAX_LENGTH}
                            </p>
                        </div>
                    )}
                    <div className={styles.closeConfirmButton}>
                        <MuiButton onClick={handleClose} colors="tertiaryText">
                            Cancel
                        </MuiButton>
                        <MuiButton
                            onClick={() => {
                                setIsConfirmOpen(false)
                                onHandleConfirm?.(shift, action, action === "reject" ? rejectionNotes : undefined)
                            }}
                        >
                            Confirm
                        </MuiButton>
                    </div>
                </div>
            </Dialog>
        </div>
    )
}

export default ShiftCard